import { DEFAULT_ALL_VALUE } from '@chronosphereio/core';
import constate from 'constate';
import { useMemo } from 'react';
import { create, useStore } from 'zustand';
import { devtools } from 'zustand/middleware';
import { immer } from 'zustand/middleware/immer';
import { shallow } from 'zustand/shallow';
import { useStoreWithEqualityFn } from 'zustand/traditional';
import { VariableStateMap } from '@/core';
import { useTemplateVariableValues } from '@/plugin-system';
import { usePanelGroup } from '../DashboardProvider';
import { useDashboardTemplateVariableValues, useProcessVariableAllState } from '../TemplateVariableProvider';

const EMPTY_SPLIT_VARIABLE_VALUES: string[] = [];

export type SplitModeArgs = {
  panelGroupId: number;
};

export type SplitModeStore = {
  enabled: boolean;
  processedVariableState: VariableStateMap;
  splitVariableName?: string;
  splitVariableValues: string[];
  setSplitModeEnabled: (value: boolean) => void;
};

function useSplitModeInternal({ panelGroupId }: SplitModeArgs) {
  const group = usePanelGroup(panelGroupId);
  const { splitVariableName } = group;

  // Lookup variable values using splitVariableName, parse to account for 'All' state
  const { processedVariableState, splitVariableValues } = useSplitVariableValues({ variableName: splitVariableName });

  return create<SplitModeStore>()(
    devtools(
      immer((set) => {
        return {
          enabled: (group.kind === 'Grid' && group.splitEnabled) ?? false,
          splitVariableName: splitVariableName,
          splitVariableValues,
          processedVariableState,
          setSplitModeEnabled: (value: boolean) => {
            set(
              (state) => {
                state.enabled = value;
              },
              false,
              '[Split Mode] setSplitModeEnabled'
            );
          },
        };
      }),
      { name: 'SplitModeStore' }
    )
  );
}

export const [SplitModeProvider, useSplitModeStore] = constate(useSplitModeInternal);

/**
 * Access split variable state including processed 'All' variable values.
 */
export const useSplitVariableState = () => {
  const store = useSplitModeStore();
  // useStore with three params was deprecated in favor of useStoreWithEqualityFn.
  // See: https://github.com/pmndrs/zustand/discussions/1937
  const state = useStoreWithEqualityFn(
    store,
    (state) => ({
      splitVariableName: state.splitVariableName,
      splitVariableValues: state.splitVariableValues,
      processedVariableState: state.processedVariableState,
    }),
    shallow
  );
  return state;
};

/**
 * Helper hook to get the enabled state of split mode.
 */
export function useIsSplitModeEnabled(): boolean {
  try {
    return useCheckSplitModeEnabled();
  } catch (_e) {
    return false;
  }
}

/**
 * Internal split mode enabled state lookup.
 */
function useCheckSplitModeEnabled() {
  const s = useSplitModeStore();
  return useStore(s, (state) => state.enabled);
}

/**
 * Helper hook to override split mode enabled state.
 */
export function useSetSplitModeEnabled() {
  const s = useSplitModeStore();
  return useStore(s, (state) => state.setSplitModeEnabled);
}

/**
 * Helper hook to get the active variable name.
 */
export function useSplitVariableName() {
  const s = useSplitModeStore();
  return useStore(s, (state) => state.splitVariableName);
}

/**
 * Helper hook to get the variable value for the first aka 'Main' split panel group
 */
export function useMainVariableValue() {
  const s = useSplitModeStore();
  return useStore(s, (state) => state.splitVariableValues[0]);
}

/**
 * Helper hook to get the variable values for the split panel group
 */
export function useSplitVariableValues({ variableName }: { variableName?: string }) {
  // Accounts for 'All' in List variables, which can include variables inside custom_all_value
  const processedVariableState = useProcessVariableAllState();
  const originalVariableValues = useDashboardTemplateVariableValues();

  // Lookup variable values using splitVariableName, parse to account for 'All' state
  const splitVariableValues = useMemo(() => {
    if (!variableName) {
      return [];
    }
    return calculateSplitVariableValues(variableName, originalVariableValues, processedVariableState);
  }, [variableName, originalVariableValues, processedVariableState]);
  return { processedVariableState, splitVariableValues };
}

/**
 * Utility to get variable values using splitVariableName save in panel group definition.
 * Also accounts for 'All' state by passing values instead of '$__all'.
 */
export function calculateSplitVariableValues(
  splitVariableName: string | undefined,
  originalVariableValues: VariableStateMap,
  processedVariableState: VariableStateMap
): string[] {
  if (!splitVariableName) return EMPTY_SPLIT_VARIABLE_VALUES;

  const splitVariableState = processedVariableState[splitVariableName];
  if (!splitVariableState) return EMPTY_SPLIT_VARIABLE_VALUES;

  const originalVariableState = originalVariableValues[splitVariableName];
  let splitVariableValues: string[] = [];
  if (originalVariableState?.value) {
    splitVariableValues = Array.isArray(originalVariableState.value)
      ? originalVariableState.value
      : [originalVariableState.value];
  }

  // If $__all is selected for the split variable, replace with actual options so we can loop through them in SplitGridLayout.
  if (splitVariableValues[0] === DEFAULT_ALL_VALUE && splitVariableState?.options) {
    splitVariableValues = splitVariableState.options.map((option) => option.value);
  }

  return splitVariableValues;
}

/**
 * Lookup relevant state to tell if the current component is part of the "Main" panel group.
 */
export function useCheckSplitGroup() {
  const splitVariableName = useSplitVariableName();
  const currentVariableState = useTemplateVariableValues([splitVariableName ?? '']);
  const mainVariableValue = useMainVariableValue();
  if (splitVariableName !== undefined) {
    // Check if scoped variable for current panel group matches the 'Main' split variable value.
    const splitVariableValue = currentVariableState[splitVariableName]?.value;
    return {
      isMainPanelGroup: splitVariableValue === mainVariableValue,
      splitVariableName,
      splitVariableValue,
    };
  }
  // Scoped variable does not match 'Main' variable so must be a dynamic splited panel group.
  return { isMainPanelGroup: false };
}
